<template>
    <div class="search-preview" :class="{ 'search-preview--loading': isLoading }">
        <CampaignDataProvider
            :key="searchOptions.searchId + '-' + refreshCounter"
            :refresh-counter="refreshCounter"
            :options="requestOptions"
            @data="onData"
            @loading="onLoading"
            @error="onError"
        />

        <hox-alert v-if="errorMessage" type="danger" size="small" margin-top="none" margin-bottom="none">
            <template #title>
                <span :title="errorMessage">
                    Sorry, the generated content was rejected by our automated content moderation system.
                </span>
            </template>
            <template #content />
            <template #actionItems>
                <Button type="primary" @click="forceRefetch">Try again</Button>
            </template>
        </hox-alert>

        <div v-else class="search-preview__images">
            <div v-for="outputId in outputImgCount" :key="outputId" class="search-preview__image">
                <hox-loading-spinner
                    v-if="isLoading"
                    :size="70"
                    class="search-preview__image-loader"
                    icon="wish-icon"
                />

                <template v-else>
                    <div class="search-preview__image-hover">
                        <a class="ivu-btn search-preview__button" :href="getImageUrl(outputId)" target="_blank">
                            Open in new window
                        </a>
                        <Button @click="onUseImage(getImageUrl(outputId))">Add to Library</Button>
                    </div>
                    <img class="search-preview__image-img" :src="getImageUrl(outputId)" />
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import CampaignDataProvider from "@/components/Campaign/CampaignDataProvider.vue";

import { type AiCustomInputs, getRequestOptions } from "@/enums/ai";
import type {
    ResourceManagerFailedResponse,
    ResourceManagerSuccessResponse
} from "@/utils/CreativeIntelligenceApi/resourceManager";

export default defineComponent({
    name: "SearchPreview",
    components: { CampaignDataProvider },
    props: {
        searchOptions: {
            type: Object as PropType<AiCustomInputs>,
            required: true
        }
    },

    emits: ["useImage", "complete"],

    data() {
        return {
            isLoading: true,
            data: null as null | ResourceManagerFailedResponse | ResourceManagerSuccessResponse<string[]>,
            errorMessage: "",
            refreshCounter: 0
        };
    },

    computed: {
        outputImgCount() {
            return this.requestOptions.input.num_images;
        },

        requestOptions() {
            return getRequestOptions(this.searchOptions, this.refreshCounter);
        }
    },

    methods: {
        forceRefetch() {
            this.errorMessage = "";
            this.isLoading = true;
            this.refreshCounter += 1;
        },

        onData(search: ResourceManagerFailedResponse | ResourceManagerSuccessResponse<string[]>) {
            this.data = search;

            if (Object.hasOwn(search.output, "fail")) {
                this.errorMessage = (search as ResourceManagerFailedResponse).output.fail as string;
                this.isLoading = false;
            }

            this.$emit("complete");
        },

        onUseImage(url: string) {
            this.$emit("useImage", url);
        },

        onError({ message }: { message: string | { errorMessage: string } }) {
            if (typeof message === "string") {
                this.errorMessage = message;
            } else if (message.errorMessage) {
                this.errorMessage = message.errorMessage;
            }

            this.isLoading = false;
            this.$emit("complete");
        },

        onLoading(loading: boolean) {
            this.isLoading = loading;
        },

        getImageUrl(id: number) {
            return (this.data as null | ResourceManagerSuccessResponse<string[]>)?.output.success[id - 1];
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.search-preview {
    flex: 1 1 24%;
    margin: auto 0;
    max-height: 100%;
    &__images {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    &__button {
        display: flex;
        align-items: center;
        margin-bottom: $spacing-small;
    }

    &__add-library {
        position: absolute;
        top: 0;
        left: 0;
        padding: 10px;
        width: 100%;
        color: #fff;
        background: rgba(0, 0, 0, 0.5);
    }

    &--loading {
        .search-preview__image {
            border: 1px solid #e8eaec;
            border-radius: 4px;
        }
    }

    &:hover {
        .imagine-carousel-arrow {
            visibility: visible;
        }
    }
}

.search-preview__image {
    flex: 1 1 24%;
    object-fit: contain;
    aspect-ratio: 1 / 1;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    &-img {
        object-fit: contain;
        width: 100%;
        height: 100%;
        max-height: 500px;
    }

    &:hover .search-preview__image-hover {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        a,
        button {
            @media (max-width: 1200px) {
                font-size: 1vw;
            }
            @media (max-width: 800px) {
                font-size: 10px;
            }
        }
    }
}

.search-preview__image-hover {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    background-color: #ffffff66;
    width: 100%;
    height: 100%;
}
</style>
